// frontend/src/App.jsx
import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { ROUTES } from "./config";
import { ToastProvider } from "./components/common/Toast";
import ErrorBoundary from "./components/common/ErrorBoundary";
import BackendStatus from "./components/common/BackendStatus";
import Spinner from "./components/common/Spinner";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import ConsentNotice from "./components/layout/ConsentNotice";
import LoginModal from "./components/auth/LoginModal";
import LandingPage from "./pages/LandingPage";
import NotFound from "./pages/NotFound";

// Owned by other workstreams; lazy so they never weigh on the landing bundle.
const ChatPage = lazy(() => import("./pages/ChatPage"));
const AdminApp = lazy(() => import("./pages/admin/AdminApp"));

const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/legal/TermsOfService"));
const CookieNotice = lazy(() => import("./pages/legal/CookieNotice"));
const AcceptableUse = lazy(() => import("./pages/legal/AcceptableUse"));
const AIDisclaimer = lazy(() => import("./pages/legal/AIDisclaimer"));
const DataRetention = lazy(() => import("./pages/legal/DataRetention"));

function PageLoader({ label = "Loading" }) {
  return (
    <div className="app-loading">
      <Spinner size={22} label={label} />
    </div>
  );
}

/** Scroll to top on path change (hash links are handled by the landing page). */
function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

/** Redirects to /login (landing + login modal) when signed out. */
export function RequireAuth({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoader label="Checking your session" />;
  if (!isAuthenticated) return <Navigate to={ROUTES.login} replace state={{ from: location }} />;
  return children;
}

/** Header + main + footer for public pages; owns the /login modal. */
function PublicLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const isLogin = location.pathname === ROUTES.login;
  const isLanding = location.pathname === ROUTES.home || isLogin;
  const from = location.state?.from;
  const redirectTo = from?.pathname && from.pathname !== ROUTES.login ? `${from.pathname}${from.search || ""}` : ROUTES.app;

  if (isLogin && !loading && isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <>
      <a href="#main" className="skip-link">Skip to content</a>
      <Header variant={isLanding ? "landing" : "site"} />
      <main id="main" tabIndex={-1} className="site-main">
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
      <ConsentNotice />
      <LoginModal
        open={isLogin && !isAuthenticated}
        onClose={() => navigate(ROUTES.home, { replace: true })}
        onSuccess={() => navigate(redirectTo, { replace: true })}
      />
    </>
  );
}

function ChatRoute() {
  return (
    <RequireAuth>
      <Suspense fallback={<PageLoader label="Loading workspace" />}>
        <ChatPage />
      </Suspense>
    </RequireAuth>
  );
}

function AppRoutes() {
  return (
    <>
      <ScrollManager />
      <BackendStatus />
      <Routes>
        <Route element={<PublicLayout />}>
          <Route index element={<LandingPage />} />
          <Route path="login" element={<LandingPage />} />
          <Route path="privacy" element={<PrivacyPolicy />} />
          <Route path="terms" element={<TermsOfService />} />
          <Route path="cookies" element={<CookieNotice />} />
          <Route path="acceptable-use" element={<AcceptableUse />} />
          <Route path="ai-disclaimer" element={<AIDisclaimer />} />
          <Route path="data-retention" element={<DataRetention />} />
          <Route path="*" element={<NotFound />} />
        </Route>
        <Route path="/app" element={<ChatRoute />} />
        <Route path="/app/:sessionId" element={<ChatRoute />} />
        <Route
          path="/admin/*"
          element={
            <Suspense fallback={<PageLoader label="Loading admin" />}>
              <AdminApp />
            </Suspense>
          }
        />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
            <ErrorBoundary>
              <AppRoutes />
            </ErrorBoundary>
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
